import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/cn'
import { Status, type StatusTone } from '../../atoms/status/status'
import { Profile, type ProfilePerson } from '../../molecules/profile/profile'

/**
 * The Work Order Stage Card (498:8537): a white card, 16px radius, 24px padding,
 * drawn 392px wide. "Stage" in Body Light dark with the Status on the right, its
 * text overridden to Body SemiBold (Status `size="md"`); a 1px rule in Background
 * (#F3F3F3, not Stroke) 24px below; then 24px below that a row per person,
 * 16px apart, with the role in Body Light dark and the Profile on the right.
 *
 * The names are drawn in the Components board's second "Dark" (#080712), which
 * is what the Profile molecule renders.
 */
export const workOrderStageCardVariants = cva(
	'flex flex-col items-start rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans',
)

/** A person on the work order and their role, e.g. "Designer". */
export interface WorkOrderStageAssignee {
	role: string
	/** The assignee; omit it for "Unassigned". */
	person?: ProfilePerson | null
}

export interface WorkOrderStageCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	/**
	 * The work order's status. Statuses are workflow data, so the screen maps the
	 * status to a tone and passes its configured label.
	 */
	status: { label: string; tone?: StatusTone }
	stageLabel?: string
	/** The people rows, as drawn Designer then PM. */
	assignees: WorkOrderStageAssignee[]
	unassignedLabel?: string
}

/** A work order's current stage and who it is assigned to, as description lists. */
export function WorkOrderStageCard({
	status,
	stageLabel = 'Stage',
	assignees,
	unassignedLabel,
	className,
	...props
}: WorkOrderStageCardProps) {
	return (
		<div {...props} className={cn(workOrderStageCardVariants(), className)}>
			<div className="flex w-full flex-col gap-6">
				<dl className="flex w-full items-center justify-between">
					<dt className="shrink-0 whitespace-nowrap text-atlas-body text-atlas-light-dark">{stageLabel}</dt>
					<dd className="flex min-w-0 justify-end">
						<Status size="md" label={status.label} tone={status.tone} />
					</dd>
				</dl>
				{/* The Figma line is 0px tall with its 1px stroke drawn above it, so it adds no height. */}
				<hr className="-mt-px w-full border-t border-atlas-background" />
				<dl className="flex w-full flex-col gap-4">
					{assignees.map((assignee) => (
						<div key={assignee.role} className="flex w-full items-start gap-1">
							<dt className="min-w-0 flex-1 text-atlas-body text-atlas-light-dark">{assignee.role}</dt>
							<dd className="shrink-0">
								<Profile person={assignee.person} unassignedLabel={unassignedLabel} />
							</dd>
						</div>
					))}
				</dl>
			</div>
		</div>
	)
}
